import React, { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useWindowSize } from 'usehooks-ts';

import { SecondaryButton } from 'components/common/button/SecondaryButton';
import { isMobile } from 'utils/browser';

import { CheckmarkIcon } from '../../../../components/icons/CheckmarkIcon';
import { ChevronDown } from '../icons/ChevronDown';

export type DropdownItem = {
  id: string;
  value: string;
};

interface Props {
  className?: string;
  controllerRef?: React.RefObject<HTMLElement>;
  dropdownMaxHeight?: number;
  getOptionValueClassName?: (option: DropdownItem) => string;
  getOptionValueWrapperClassName?: (option: DropdownItem) => string;
  handleSelectOption: (value: DropdownItem) => void;
  isOpen?: boolean;
  menuClassName?: string;
  menuOpenClassName?: string;
  mobileEnabled?: boolean;
  onToggle?: () => void;
  openUpwards?: boolean;
  optionClassName?: string;
  options: DropdownItem[];
  selectedValue: DropdownItem;
  showCheckmark?: boolean;
  valueClassName?: string;
}

const focusVisibleClass = 'focus-visible:ring-2 focus-visible:ring-[#1D76ED] focus-visible:ring-offset-bg-sf-prim-w-b';

export const Dropdown: React.FC<PropsWithChildren<Props>> = ({
  className,
  controllerRef,
  dropdownMaxHeight = 320,
  getOptionValueClassName,
  getOptionValueWrapperClassName,
  handleSelectOption,
  isOpen: controlledIsOpen,
  menuClassName,
  menuOpenClassName,
  mobileEnabled = false,
  onToggle,
  openUpwards = false,
  optionClassName,
  options,
  selectedValue,
  showCheckmark = true,
  valueClassName
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [internalMenuOpen, setInternalMenuOpen] = React.useState(false);

  const isControlled = controlledIsOpen !== undefined;
  const menuOpen = isControlled ? controlledIsOpen : internalMenuOpen;

  const isMobileDevice = mobileEnabled && isMobile();

  const handleToggleMenu = () => {
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalMenuOpen(!internalMenuOpen);
    }
  };

  const selected = useMemo(() => options.find(option => option.id === selectedValue.id), [options, selectedValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickOnWrapper = wrapperRef.current && wrapperRef.current.contains(event.target as Node);
      const isClickOnController = controllerRef?.current && controllerRef.current.contains(event.target as Node);

      if (!isClickOnWrapper && !isClickOnController) {
        if (isControlled) {
          if (controlledIsOpen) {
            onToggle?.();
          }
        } else {
          setInternalMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isControlled, controlledIsOpen, onToggle, controllerRef]);

  const handleOptionClick = (option: DropdownItem) => {
    handleSelectOption(option);
    if (isControlled) {
      onToggle?.();
    } else {
      setInternalMenuOpen(false);
    }
  };

  const { height: windowHeight } = useWindowSize();

  const isBottomDirection = useMemo(() => {
    if (!menuOpen) {
      return false;
    }

    const margin = 8;
    const parentEl = menuRef.current?.offsetParent as HTMLElement | null;
    const menuPageOffset = parentEl?.offsetTop || 0;
    const menuPageHeight = (menuRef.current?.offsetHeight || 0) + (parentEl?.offsetHeight || 0) + margin;
    const menuBottomOffset = menuPageOffset + menuPageHeight;

    return windowHeight > menuBottomOffset;
  }, [windowHeight, menuOpen]);

  return (
    <div ref={wrapperRef}>
      <div className="relative">
        <SecondaryButton
          innerClassName="gap-1.5"
          className={twMerge('pl-5 pr-4', menuOpen && 'bg-sf-sec-hov-0-90 border-sf-prim-hov-w-b', className, menuOpen && menuOpenClassName)}
          onClick={handleToggleMenu}
          size="small"
        >
          <span className={twMerge('text-subheading-6 text-icn-txt-prim', valueClassName)}>{selected?.value}</span>
          <ChevronDown className={twMerge('w-4 h-4 flex-shrink-0 text-icn-txt-prim', menuOpen && 'rotate-180')} />
        </SecondaryButton>

        {isMobileDevice && menuOpen && (
          <div
            className="fixed inset-0 bg-bg-dimmer transition-opacity z-[9]"
            onClick={() => {
              if (isControlled) {
                onToggle?.();
              } else {
                setInternalMenuOpen(false);
              }
            }}
          />
        )}

        <div
          ref={menuRef}
          className={twMerge(
            'absolute z-10 mt-0.5 overflow-auto rounded-xl bg-bg-prim-w-90 border border-brdr-prim-10-80',
            'shadow-[0px_4px_12px_-4px_rgba(0,0,0,0.16)] dark:shadow-[0px_6px_16px_-6px_rgba(0,0,0,0.64)]',
            'p-1 gap-0.5 flex flex-col',
            (openUpwards || !isBottomDirection) && 'bottom-10',
            !menuOpen && 'invisible',
            isMobileDevice && 'fixed top-auto bottom-0 w-auto max-h-[40vh] left-0 right-0 mx-2 mb-2 overflow-y-auto border-x-2 border-y-4 border-bg-prim-w-90',
            isMobileDevice && 'rounded-xl p-2 py-3 gap-1 speechify-scrollbar',
            !isMobileDevice && 'min-w-[160px] right-0',
            menuClassName
          )}
          style={!isMobileDevice ? { maxHeight: dropdownMaxHeight } : {}}
        >
          {options.map((option, index) => {
            const isSelected = selectedValue.id === option.id;
            const optionValueClassName = getOptionValueClassName?.(option);
            const optionValueWrapperClassName = getOptionValueWrapperClassName?.(option);

            return (
              <button
                key={`${option.value}-${index}`}
                className={twMerge(
                  'cursor-pointer px-3 py-2.5 min-w-[105px] rounded-lg',
                  isSelected ? 'bg-sf-prim-hov-w-90' : 'hover:bg-sf-prim-hov-w-90 active:bg-sf-prim-pres-w-90',
                  'outline-none',
                  isMobileDevice && 'py-3 min-h-[44px] text-left',
                  optionClassName,
                  focusVisibleClass
                )}
                onClick={() => handleOptionClick(option)}
              >
                <div className={twMerge('flex items-center', showCheckmark && 'gap-2', optionValueWrapperClassName)}>
                  {showCheckmark ? isSelected ? <CheckmarkIcon /> : <div className="size-5" /> : null}
                  <span className={twMerge('text-subheading-6 text-icn-txt-prim whitespace-nowrap', optionValueClassName)}>{option.value}</span>
                  {showCheckmark && <div className="size-5" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
